import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { BlockDef } from "./block.interfaces";
import { arrayField, createBlock, headingField, htmlField } from "./blockBuilder";
import { useDefaultColors } from "./useDefaultColors";
import { CenteredContact, type CenteredContactProps } from "@/views/blocks/contact/contact";
import { useFieldConfig } from "./useFieldConfig";

export function useContact() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    const centeredContactBlock = computed<BlockDef>(() =>
        createBlock<CenteredContactProps>({
            typeId: "CenteredContact",
            type: "contact",
            name: t("page.blocks.contact.centered"),
            component: CenteredContact,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Contact Us", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
                    translations.value.description
                )
            )
            .withArrayField(
                "fields",
                arrayField(
                    translations.value.field,
                    "formFields",
                    {
                        label: { label: translations.value.label, type: "text" },
                        placeholder: { label: translations.value.placeholder, type: "text" },
                        inputName: { label: translations.value.inputName, type: "text" },
                        isRequired: { label: translations.value.isRequired, type: "switch" },
                        type: {
                            label: translations.value.type,
                            type: "select",
                            options: [
                                { value: "text", label: translations.value.fieldType_text },
                                { value: "email", label: translations.value.fieldType_email },
                                { value: "tel", label: translations.value.fieldType_tel },
                                {
                                    value: "textarea",
                                    label: translations.value.fieldType_textarea,
                                },
                            ],
                        },
                        spanFull: { label: translations.value.spanFull, type: "switch" },
                    },
                    [
                        {
                            label: "Company",
                            placeholder: "Enter your company name",
                            inputName: "company",
                            isRequired: false,
                            type: "text",
                            spanFull: true,
                        },
                        {
                            label: "Email",
                            placeholder: "Enter your email",
                            inputName: "email",
                            isRequired: true,
                            type: "email",
                            spanFull: true,
                        },
                        {
                            label: "Phone number",
                            placeholder: "Enter your phone number",
                            inputName: "phone-number",
                            isRequired: false,
                            type: "tel",
                            spanFull: true,
                        },
                        {
                            label: "Message",
                            placeholder: "How can we help you?",
                            inputName: "message",
                            isRequired: true,
                            type: "textarea",
                            spanFull: true,
                        },
                    ],
                    {
                        label: "New field",
                        placeholder: "Enter your field name",
                        inputName: "new-field",
                        isRequired: false,
                        type: "text",
                        spanFull: true,
                    }
                )
            )
            .withField("privacyPolicy", {
                label: translations.value.privacyPolicy,
                group: "options",
                fields: [
                    {
                        value: "text",
                        type: "text",
                        label: translations.value.text,
                    },
                    {
                        value: "linkText",
                        type: "text",
                        label: translations.value.urlLabel,
                    },
                    {
                        value: "href",
                        type: "text",
                        label: translations.value.href,
                    },
                    {
                        value: "validationMessage",
                        type: "text",
                        label: translations.value.validationMessage,
                    },
                ],
                defaultData: {
                    data: {
                        text: "By submitting this form you agree to our",
                        linkText: "privacy policy",
                        href: "#",
                        validationMessage: "Please agree to the privacy policy to continue.",
                    },
                    isHidden: false,
                },
            })
            .withField("submitButton", {
                label: translations.value.submitButton,
                group: "buttons",
                fields: [
                    {
                        value: "label",
                        type: "text",
                        label: translations.value.label,
                    },
                    {
                        value: "hookUrl",
                        type: "text",
                        label: translations.value.hookUrl,
                    },
                    {
                        value: "errorMessage",
                        type: "text",
                        label: translations.value.errorMessage,
                    },
                    {
                        value: "successMessage",
                        type: "text",
                        label: translations.value.successMessage,
                    },
                ],
                defaultData: {
                    data: {
                        label: "Send message",
                        hookUrl: "#",
                        errorMessage: "Something went wrong. Please contact us directly via email.",
                        successMessage: "Message sent successfully. We'll get back to you soon.",
                    },
                    isHidden: false,
                },
            })
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    return {
        blocks: computed(() => [centeredContactBlock.value]),
    };
}
